import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OfflineWalletComponent = ({ user }) => {
  const [tab, setTab] = useState('wallet');
  const [offlineWallet, setOfflineWallet] = useState(null);
  const [mainBalance, setMainBalance] = useState(0);
  const [pendingSync, setPendingSync] = useState(0);
  const [loadAmount, setLoadAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrAmount, setQrAmount] = useState('');
  const [qrNote, setQrNote] = useState('');
  const [scanMode, setScanMode] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const [receiveResult, setReceiveResult] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const videoRef = useRef(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [walletRes, txRes] = await Promise.all([
        axios.get(`${API}/offline/wallet`),
        axios.get(`${API}/offline/transactions`)
      ]);
      setOfflineWallet(walletRes.data.offline_wallet);
      setMainBalance(walletRes.data.main_balance || 0);
      setPendingSync(walletRes.data.pending_sync || 0);
      setTransactions(txRes.data.transactions || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadWallet = async () => {
    if (!loadAmount || parseFloat(loadAmount) <= 0) return;
    setLoading(true);
    try {
      const deviceId = `${navigator.userAgent.slice(0,20)}_${screen.width}x${screen.height}`;
      await axios.post(`${API}/offline/wallet/load`, {
        amount: parseFloat(loadAmount),
        device_id: deviceId
      });
      setLoadAmount('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al cargar');
    } finally { setLoading(false); }
  };

  const unloadWallet = async () => {
    if (!window.confirm('Descargar billetera offline? El saldo restante vuelve a tu billetera principal.')) return;
    setLoading(true);
    try {
      await axios.post(`${API}/offline/wallet/unload`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally { setLoading(false); }
  };

  const generateQR = async () => {
    if (!qrAmount || parseFloat(qrAmount) <= 0) return;
    setLoading(true);
    setQrData(null);
    try {
      const res = await axios.post(`${API}/offline/qr/generate`, {
        amount: parseFloat(qrAmount),
        note: qrNote
      });
      setQrData(res.data);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally { setLoading(false); }
  };

  const receiveQR = async () => {
    if (!scanInput.trim()) return;
    setLoading(true);
    setReceiveResult(null);
    try {
      const res = await axios.post(`${API}/offline/qr/receive`, { qr_data: scanInput });
      setReceiveResult(res.data);
      setScanInput('');
      fetchData();
    } catch (err) {
      setReceiveResult({ error: err.response?.data?.detail || 'QR invalido' });
    } finally { setLoading(false); }
  };

  const startScanner = async () => {
    setScanMode(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      setTimeout(() => {
        const scanner = new Html5Qrcode('qr-reader');
        scannerRef.current = scanner;
        scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (text) => {
            scanner.stop().catch(() => {});
            setScanInput(text);
            setScanMode(false);
            // Auto-receive
            setLoading(true);
            try {
              const res = await axios.post(`${API}/offline/qr/receive`, { qr_data: text });
              setReceiveResult(res.data);
              fetchData();
            } catch (err) {
              setReceiveResult({ error: err.response?.data?.detail || 'QR invalido' });
            } finally { setLoading(false); }
          }
        ).catch(() => setScanMode(false));
      }, 300);
    } catch (err) {
      setScanMode(false);
      alert('No se pudo acceder a la camara');
    }
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
    }
    setScanMode(false);
  };

  const syncTransactions = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${API}/offline/sync`);
      setSyncResult(res.data);
      fetchData();
    } catch (err) {
      setSyncResult({ error: err.response?.data?.detail || 'Error de sincronizacion' });
    } finally { setSyncing(false); }
  };

  const tabs = [
    { id: 'wallet', label: 'Billetera', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    { id: 'send', label: 'Enviar QR', icon: 'M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z' },
    { id: 'receive', label: 'Recibir', icon: 'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z M15 13a3 3 0 11-6 0 3 3 0 016 0z' },
    { id: 'history', label: 'Historial', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' }
  ];

  return (
    <div className="space-y-6" data-testid="offline-wallet-module">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-zinc-900 rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12 20.5h.01"/>
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold">Billetera Offline</h2>
              <p className="text-sm text-slate-400">Transacciones sin internet via QR</p>
            </div>
          </div>

          {/* Connection status */}
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mt-2 ${
            isOnline ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'
          }`} data-testid="connection-status">
            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
            {isOnline ? 'En linea' : 'Sin conexion'}
          </div>

          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="bg-white/5 rounded-xl p-3 backdrop-blur-sm border border-white/10">
              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Billetera Principal</p>
              <p className="text-lg font-bold text-white">{mainBalance.toFixed(2)} <span className="text-xs text-slate-400">Cc</span></p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 backdrop-blur-sm border border-white/10">
              <p className="text-[10px] text-amber-400 uppercase tracking-wide">Offline</p>
              <p className="text-lg font-bold text-amber-300">{offlineWallet ? offlineWallet.current_balance.toFixed(2) : '0.00'} <span className="text-xs text-slate-400">Cc</span></p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 backdrop-blur-sm border border-white/10">
              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Por Sincronizar</p>
              <p className="text-lg font-bold text-white">{pendingSync}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`offline-tab-${t.id}`}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium transition-all ${
              tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d={t.icon} />
            </svg>
            {t.label}
          </button>
        ))}
      </div>

      {/* Wallet Tab */}
      {tab === 'wallet' && (
        <div className="space-y-4">
          {!offlineWallet ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center" data-testid="load-wallet-section">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-50 flex items-center justify-center">
                <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">Cargar Billetera Offline</h3>
              <p className="text-sm text-gray-500 mb-4">Transfiere CUALcoin de tu billetera principal a la offline. Este saldo queda bloqueado y solo se puede usar via QR.</p>
              <div className="max-w-xs mx-auto space-y-3">
                <div className="relative">
                  <input type="number" value={loadAmount} onChange={e => setLoadAmount(e.target.value)}
                    placeholder="Monto a cargar" min="1" max="10000" data-testid="load-amount-input"
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl text-center text-lg font-medium focus:ring-2 focus:ring-amber-300 focus:border-amber-400" />
                  <span className="absolute left-4 top-3.5 text-sm font-bold text-gray-400">Cc</span>
                </div>
                <p className="text-xs text-gray-400">Disponible: {mainBalance.toFixed(2)} Cc | Max: 10,000 Cc</p>
                <button onClick={loadWallet} disabled={loading || !loadAmount || parseFloat(loadAmount) <= 0}
                  data-testid="load-wallet-btn"
                  className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 disabled:opacity-40 transition-all shadow-lg">
                  {loading ? 'Cargando...' : 'Cargar Billetera Offline'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="active-wallet-section">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-gray-900">Billetera Activa</h3>
                  <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium">ACTIVA</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Cargado:</span> <span className="font-medium">{offlineWallet.loaded_amount} Cc</span></div>
                  <div><span className="text-gray-500">Disponible:</span> <span className="font-bold text-amber-600">{offlineWallet.current_balance.toFixed(2)} Cc</span></div>
                  <div><span className="text-gray-500">Gastado:</span> <span className="font-medium text-red-500">{offlineWallet.spent.toFixed(2)} Cc</span></div>
                  <div><span className="text-gray-500">Recibido:</span> <span className="font-medium text-emerald-500">{offlineWallet.received.toFixed(2)} Cc</span></div>
                  <div><span className="text-gray-500">Transacciones:</span> <span className="font-medium">{offlineWallet.transactions_count}</span></div>
                  <div><span className="text-gray-500">ID:</span> <span className="font-mono text-xs">{offlineWallet.wallet_id}</span></div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={syncTransactions} disabled={syncing || pendingSync === 0} data-testid="sync-btn"
                  className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-40 flex items-center justify-center gap-2">
                  <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  Sincronizar ({pendingSync})
                </button>
                <button onClick={unloadWallet} disabled={loading} data-testid="unload-btn"
                  className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-300 disabled:opacity-40">
                  Descargar
                </button>
              </div>

              {syncResult && (
                <div className={`p-3 rounded-xl text-sm ${syncResult.error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                  {syncResult.error || syncResult.message}
                </div>
              )}
            </div>
          )}

          {/* Security Info */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h4 className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Seguridad</h4>
            <ul className="space-y-1.5 text-xs text-gray-500">
              <li className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">&#9679;</span> Los fondos se bloquean en el servidor al cargar la billetera offline</li>
              <li className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">&#9679;</span> Cada transaccion se firma con SHA-256 para evitar alteraciones</li>
              <li className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">&#9679;</span> Solo puedes gastar lo que pre-cargaste: proteccion anti doble gasto</li>
              <li className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">&#9679;</span> Al reconectar, todo se sincroniza con la bitacora y CUALchain</li>
            </ul>
          </div>
        </div>
      )}

      {/* Send QR Tab */}
      {tab === 'send' && (
        <div className="space-y-4">
          {!offlineWallet ? (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">Carga tu billetera offline primero</p>
              <button onClick={() => setTab('wallet')} className="text-amber-600 text-sm font-medium mt-2 hover:underline">Ir a Billetera</button>
            </div>
          ) : qrData ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center" data-testid="qr-generated">
              <h3 className="text-base font-bold text-gray-900 mb-1">QR Generado</h3>
              <p className="text-sm text-gray-500 mb-4">El destinatario debe escanear este codigo</p>
              <div className="inline-block p-4 bg-white rounded-2xl shadow-lg border border-gray-100">
                <QRCodeSVG value={qrData.qr_data} size={220} level="M"
                  bgColor="#ffffff" fgColor="#1e293b" />
              </div>
              <div className="mt-4 space-y-1">
                <p className="text-2xl font-bold text-gray-900">{qrData.amount} Cc</p>
                <p className="text-xs text-gray-400">TX: {qrData.tx_id}</p>
                <p className="text-xs text-amber-600">Saldo offline restante: {qrData.remaining_offline.toFixed(2)} Cc</p>
              </div>
              <button onClick={() => setQrData(null)} className="mt-4 px-6 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm hover:bg-gray-200">
                Nuevo QR
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="qr-form">
              <h3 className="text-sm font-bold text-gray-900">Generar QR de Pago</h3>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Monto (CUALcoin)</label>
                <input type="number" value={qrAmount} onChange={e => setQrAmount(e.target.value)}
                  placeholder="0.00" min="0.01" data-testid="qr-amount-input"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-lg font-medium focus:ring-2 focus:ring-amber-300" />
                <p className="text-xs text-gray-400 mt-1">Disponible offline: {offlineWallet.current_balance.toFixed(2)} Cc</p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Nota (opcional)</label>
                <input type="text" value={qrNote} onChange={e => setQrNote(e.target.value)} maxLength="100"
                  placeholder="Descripcion del pago..." data-testid="qr-note-input"
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-300" />
              </div>
              <button onClick={generateQR} disabled={loading || !qrAmount || parseFloat(qrAmount) <= 0}
                data-testid="generate-qr-btn"
                className="w-full py-3 bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-xl font-semibold hover:from-slate-900 disabled:opacity-40 flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"/>
                </svg>
                Generar QR
              </button>
            </div>
          )}
        </div>
      )}

      {/* Receive Tab */}
      {tab === 'receive' && (
        <div className="space-y-4">
          {scanMode ? (
            <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="scanner-active">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-900">Escaneando...</h3>
                <button onClick={stopScanner} className="text-xs text-red-500 hover:text-red-700">Cancelar</button>
              </div>
              <div id="qr-reader" className="rounded-xl overflow-hidden" style={{ width: '100%' }} />
            </div>
          ) : (
            <div className="space-y-4">
              <button onClick={startScanner} data-testid="start-scanner-btn"
                className="w-full py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-xl font-semibold hover:from-slate-900 flex items-center justify-center gap-3">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                Escanear QR con Camara
              </button>

              <div className="relative flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">o pegar datos del QR</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                <textarea value={scanInput} onChange={e => setScanInput(e.target.value)}
                  placeholder='Pega aqui los datos del QR...' rows={3} data-testid="qr-paste-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs font-mono focus:ring-2 focus:ring-amber-300 resize-none" />
                <button onClick={receiveQR} disabled={loading || !scanInput.trim()} data-testid="receive-qr-btn"
                  className="w-full py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-40">
                  Recibir Transferencia
                </button>
              </div>
            </div>
          )}

          {receiveResult && (
            <div className={`p-4 rounded-xl border ${receiveResult.error ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="receive-result">
              {receiveResult.error ? (
                <p className="text-sm text-red-700">{receiveResult.error}</p>
              ) : (
                <div>
                  <p className="text-sm font-medium text-emerald-800">{receiveResult.message}</p>
                  <p className="text-xs text-emerald-600 mt-1">TX: {receiveResult.tx_id} | Acreditado en: {receiveResult.credited_to === 'offline' ? 'Billetera Offline' : 'Billetera Principal'}</p>
                  {receiveResult.note && <p className="text-xs text-gray-500 mt-1">Nota: {receiveResult.note}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <div className="space-y-2">
          {transactions.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-sm">Sin transacciones offline</p>
            </div>
          ) : (
            transactions.map((t, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3" data-testid={`offline-tx-${t.tx_id}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                  t.direction === 'sent' ? 'bg-red-100' : 'bg-emerald-100'
                }`}>
                  <svg className={`w-4 h-4 ${t.direction === 'sent' ? 'text-red-500' : 'text-emerald-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={t.direction === 'sent' ? 'M5 10l7-7m0 0l7 7m-7-7v18' : 'M19 14l-7 7m0 0l-7-7m7 7V3'} />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{t.counterpart}</p>
                  <div className="flex items-center gap-2 text-[10px] text-gray-400">
                    <span className={`px-1.5 py-0.5 rounded ${t.synced ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>
                      {t.synced ? 'Sincronizado' : 'Pendiente'}
                    </span>
                    {t.note && <span>{t.note}</span>}
                  </div>
                </div>
                <p className={`text-sm font-bold ${t.direction === 'sent' ? 'text-red-600' : 'text-emerald-600'}`}>
                  {t.direction === 'sent' ? '-' : '+'}{t.amount.toFixed(2)} Cc
                </p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineWalletComponent;
